import React, { useState, useMemo } from "react";
import {
  Download, Search, Filter, CheckSquare, Square, ChevronDown,
  ChevronUp, FileSpreadsheet, Calendar
} from "lucide-react";
import { BookingRow, deriveStatus } from "./bookings/BookingRow";
import { exportCSV, exportExcel } from "./bookings/exportUtils";

const BookingsTable = ({ bookings = [] }) => {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [sortField, setSortField] = useState("created_at");
  const [sortDir, setSortDir] = useState("desc");
  const [showFilters, setShowFilters] = useState(false);

  const filtered = useMemo(() => {
    return bookings
      .filter(b => {
        if (statusFilter !== "all" && deriveStatus(b) !== statusFilter) return false;
        if (dateFrom && b.date < dateFrom) return false;
        if (dateTo && b.date > dateTo) return false;
        if (search) {
          const q = search.toLowerCase();
          return (
            (b.customer_name || "").toLowerCase().includes(q) ||
            (b.customer_phone || "").includes(q) ||
            (b.customer_email || "").toLowerCase().includes(q) ||
            (b.id || "").toLowerCase().includes(q) ||
            (b.package || "").toLowerCase().includes(q) ||
            (b.occasion || "").toLowerCase().includes(q) ||
            (b.address || "").toLowerCase().includes(q)
          );
        }
        return true;
      })
      .sort((a, b) => {
        const aVal = a[sortField] || "";
        const bVal = b[sortField] || "";
        return sortDir === "asc" ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
      });
  }, [bookings, search, statusFilter, dateFrom, dateTo, sortField, sortDir]);

  const allSelected = filtered.length > 0 && filtered.every(b => selectedIds.has(b.id));

  const toggleAll = () => {
    setSelectedIds(allSelected ? new Set() : new Set(filtered.map(b => b.id)));
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const handleSort = (field) => {
    if (sortField === field) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortField(field); setSortDir("desc"); }
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) return <ChevronDown size={12} className="text-gray-300" />;
    return sortDir === "asc" ? <ChevronUp size={12} className="text-[#E83E8C]" /> : <ChevronDown size={12} className="text-[#E83E8C]" />;
  };

  const getExportRows = () => filtered.filter(b => selectedIds.size === 0 || selectedIds.has(b.id));

  return (
    <div className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden" data-testid="bookings-table">
      {/* Header */}
      <div className="p-4 border-b border-gray-100">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <h3 className="text-lg font-bold text-[#1E1B4B] flex items-center gap-2">
            <FileSpreadsheet size={18} className="text-[#E83E8C]" />
            All Bookings ({filtered.length})
            {selectedIds.size > 0 && <span className="text-sm font-normal text-[#E83E8C]">({selectedIds.size} selected)</span>}
          </h3>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowFilters(!showFilters)} className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-sm text-[#475569] hover:bg-gray-50" data-testid="toggle-filters-btn">
              <Filter size={14} /> Filters
            </button>
            <button onClick={() => exportCSV(getExportRows())} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-emerald-50 text-emerald-700 text-sm font-medium hover:bg-emerald-100" data-testid="export-csv-btn">
              <Download size={14} /> CSV
            </button>
            <button onClick={() => exportExcel(getExportRows())} className="flex items-center gap-1.5 px-3 py-2 rounded-lg bg-blue-50 text-blue-700 text-sm font-medium hover:bg-blue-100" data-testid="export-excel-btn">
              <Download size={14} /> Excel
            </button>
          </div>
        </div>

        <div className="relative">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#94a3b8]" />
          <input value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name, phone, email, booking ID, package..."
            className="w-full pl-10 pr-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#E83E8C] outline-none text-sm"
            data-testid="bookings-table-search" />
        </div>

        {showFilters && (
          <div className="flex flex-wrap gap-3 mt-3 pt-3 border-t border-gray-100" data-testid="bookings-filters">
            <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm focus:border-[#E83E8C] outline-none" data-testid="status-filter">
              <option value="all">All Status</option>
              <option value="confirmed">Confirmed</option>
              <option value="pending">Pending</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
            <div className="flex items-center gap-2">
              <Calendar size={14} className="text-[#94a3b8]" />
              <input type="date" value={dateFrom} onChange={e => setDateFrom(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="date-from" />
              <span className="text-xs text-[#94a3b8]">to</span>
              <input type="date" value={dateTo} onChange={e => setDateTo(e.target.value)} className="px-3 py-2 rounded-lg border border-gray-200 text-sm" data-testid="date-to" />
            </div>
          </div>
        )}
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-[#1E1B4B]/5 text-[#475569] text-xs uppercase tracking-wider">
              <th className="p-3 text-left w-10">
                <button onClick={toggleAll} className="flex items-center" data-testid="select-all-btn">
                  {allSelected ? <CheckSquare size={16} className="text-[#E83E8C]" /> : <Square size={16} className="text-gray-300" />}
                </button>
              </th>
              {[
                { field: "id", label: "ID" },
                { field: "date", label: "Event Date" },
                { field: "setup_time", label: "Selected Setup Time" },
                { field: "customer_name", label: "Customer" },
                { field: null, label: "Contact" },
                { field: "package", label: "Package" },
                { field: null, label: "Occasion" },
                { field: null, label: "Address" },
                { field: "payment_amount", label: "Amount" },
                { field: null, label: "Payment" },
                { field: "booking_status", label: "Status" },
                { field: null, label: "Sales Exec" },
                { field: "created_at", label: "Created" },
              ].map(col => (
                <th key={col.label} className={`p-3 text-left ${col.field ? 'cursor-pointer hover:text-[#E83E8C]' : ''}`} onClick={col.field ? () => handleSort(col.field) : undefined}>
                  <span className="flex items-center gap-1">{col.label} {col.field && <SortIcon field={col.field} />}</span>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 ? (
              <tr><td colSpan={14} className="text-center py-8 text-[#94a3b8]">No bookings match your filters</td></tr>
            ) : filtered.map((b, i) => (
              <BookingRow key={b.id || i} b={b} index={i} isSelected={selectedIds.has(b.id)} onToggle={() => toggleRow(b.id)} />
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer */}
      <div className="p-3 border-t border-gray-100 flex items-center justify-between text-xs text-[#94a3b8]">
        <span>Showing {filtered.length} of {bookings.length} bookings</span>
        {selectedIds.size > 0 && (
          <span className="text-[#E83E8C] font-medium">{selectedIds.size} row(s) selected — export will include only selected rows</span>
        )}
      </div>
    </div>
  );
};

export default BookingsTable;
